import React, { lazy, Suspense, useCallback, useEffect, useRef, useState, Component } from 'react';
import axios from './utils/shopifyApi';
import "@/App.css";
import { AppProvider, useApp } from './context/AppContext';
import { RuntimeProvider, useRuntime } from './context/RuntimeContext';
import { ShopifyNavMenu, resolvePageFromUrl, pathForPage } from './components/ShopifyNavMenu';
import { Sidebar } from './components/Sidebar';
import { Progress } from './components/ui/progress';
import { Toaster } from './components/ui/sonner';
import { ReviewPopup } from './components/ReviewPopup';
import { recoveryCycleKey, shouldAttemptRecovery } from './lib/tokenRecovery';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Catches ChunkLoadError (dynamic import fails with 404/network) and prevents
// the entire app from going blank. On chunk error, reloading the page fetches
// the new bundle from the server and resolves the stale-deployment mismatch.
class PageErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const isChunk = error.name === 'ChunkLoadError' || /Loading chunk \d+ failed/.test(error.message);
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4 text-center px-6">
        <p className="text-sm text-gray-600">
          {isChunk
            ? 'Aggiornamento disponibile. Ricarica la pagina per continuare.'
            : 'Si è verificato un errore imprevisto.'}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="btn-ai text-sm px-4 py-2"
        >
          Ricarica la pagina
        </button>
      </div>
    );
  }
}

const Dashboard       = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Products        = lazy(() => import('./pages/Products').then(m => ({ default: m.Products })));
const AIContent       = lazy(() => import('./pages/AIContent').then(m => ({ default: m.AIContent })));
const Translations    = lazy(() => import('./pages/Translations').then(m => ({ default: m.Translations })));
const Images          = lazy(() => import('./pages/Images').then(m => ({ default: m.Images })));
const BatchOperations = lazy(() => import('./pages/BatchOperations').then(m => ({ default: m.BatchOperations })));
const History         = lazy(() => import('./pages/History').then(m => ({ default: m.History })));
const Settings        = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const Support         = lazy(() => import('./pages/Support').then(m => ({ default: m.Support })));
const SpeedOptimizer  = lazy(() => import('./pages/SpeedOptimizer').then(m => ({ default: m.SpeedOptimizer })));
const SearchConsole   = lazy(() => import('./pages/SearchConsole').then(m => ({ default: m.SearchConsole })));

// Page registry — id must match NAV_ROUTES ids in ShopifyNavMenu.js
const pages = {
  home: Dashboard,
  products: Products,
  content: AIContent,
  translations: Translations,
  images: Images,
  batch: BatchOperations,
  history: History,
  speed: SpeedOptimizer,
  'search-console': SearchConsole,
  settings: Settings,
  support: Support,
};

const LAST_ROUTE_KEY = 'aio_last_route';

// Last-resort banner for a broken Shopify authorization.
//
// It is deliberately hard to reach: when the backend reports an invalid
// token we first try to repair it silently, by handing the App Bridge
// session token to Shopify's Token Exchange grant. That succeeds for the
// ordinary causes (an expired or revoked offline token while the merchant
// is right here in the app), and the merchant sees nothing at all. Only
// when Shopify itself refuses — a scope change needing fresh consent, an
// uninstalled app, no session token because we are not embedded — is there
// genuinely nothing left but asking them to reauthorize.
function TokenInvalidBanner() {
  const { store, bootstrapFromSessionToken } = useApp();
  const { t } = useRuntime();
  const [status, setStatus] = useState(null);
  const [oauthRequired, setOauthRequired] = useState(false);
  // Which invalidation episode we have already attempted recovery for — keyed
  // on its own timestamp, not on the store. A refused exchange is therefore
  // never retried on the 60 s poll (Shopify's answer will not change until the
  // merchant acts), while a *later*, separate breakage still earns its own
  // attempt instead of being locked out by the first one. See lib/tokenRecovery.
  const recoveryTried = useRef(null);

  useEffect(() => {
    if (!store?.id) {
      setStatus(null);
      setOauthRequired(false);
      // No store yet. Under Managed Installation that is a brand-new merchant
      // whose record has never been written, not a broken one — and the
      // session token in this browser is the only thing that can create it.
      // Bailing out here unconditionally is what left such a merchant with an
      // app that could not initialise: the exchange was the way out, and the
      // one guard in front of it required the very record it would produce.
      //
      // initializeStore already attempts this on boot; retrying on the same
      // 60 s poll is what covers a first attempt that lost a race with App
      // Bridge finishing its handshake. Never a redirect, never a navigation.
      let cancelledBootstrap = false;
      const retry = setInterval(() => {
        if (cancelledBootstrap) return;
        bootstrapFromSessionToken?.();
      }, 60000);
      return () => { cancelledBootstrap = true; clearInterval(retry); };
    }
    let cancelled = false;
    const check = async () => {
      try {
        const r = await axios.get(`${API}/auth/token-status/${store.id}`);
        if (cancelled) return;
        setStatus(r.data);

        // `rotation_due` is the other reason to run an exchange, and it is the
        // opposite case: the token works perfectly and that is exactly the
        // problem, because it is one of the tokens that leaked. Token Exchange
        // needs the merchant's App Bridge session token, which lives only in
        // this browser, so there is no server-side path to rotating it — the
        // backend can only ask, here, and the request still carries the signed
        // session token like every other call.
        const rotationDue = Boolean(r.data?.rotation_due);

        if (r.data?.valid !== false && !rotationDue) {
          // Healthy again — via this recovery, a reinstall, or the background
          // refresh loop. Re-arm, so a future breakage gets its own attempt.
          recoveryTried.current = null;
          setOauthRequired(false);
          return;
        }

        if (!shouldAttemptRecovery(recoveryTried.current, store.id, r.data?.invalid_at)) return;
        recoveryTried.current = recoveryCycleKey(store.id, r.data?.invalid_at);
        try {
          // No store_id is sent: the backend derives the shop from the signed
          // session token alone, so this cannot be pointed at another shop.
          const rec = await axios.post(`${API}/auth/session-token-exchange`);
          if (cancelled) return;
          if (rec.data?.recovered) {
            const fresh = await axios.get(`${API}/auth/token-status/${store.id}`);
            if (!cancelled) { setStatus(fresh.data); setOauthRequired(false); }
            return;
          }
          setOauthRequired(Boolean(rec.data?.oauth_required));
        } catch (_) {
          // The recovery call itself failed: fall back to asking the merchant
          // rather than leaving a silently broken app.
          if (!cancelled) setOauthRequired(true);
        }
      } catch (e) { /* ignore */ }
    };
    check();
    const timer = setInterval(check, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [store?.id, bootstrapFromSessionToken]);

  // Silent while the token is fine, and while automatic recovery is still
  // undecided: this is a call to action, not a progress indicator.
  if (!status || status.valid !== false || !oauthRequired) return null;
  return (
    <div className="bg-amber-100 border-b border-amber-300 px-4 py-2 flex items-center justify-between gap-3 text-sm">
      <span className="text-amber-900">
        <strong>{t('app_banner.expired', 'Shopify authorization needed.')}</strong>{' '}
        {t('app_banner.reconnect_desc', "Reconnect the app to restore sync and publishing.")}
      </span>
      <button
        onClick={async () => {
          try {
            // Use the dedicated reconnect endpoint which always generates an
            // OFFLINE token URL (no grant_options[]=per-user).  This is required
            // when Shopify rejects the stored token as "non-expiring" (legacy shpat_).
            const r = await axios.get(`${API}/auth/reconnect/${store.id}`);
            if (r.data?.install_url) window.open(r.data.install_url, '_blank');
          } catch (e) { /* ignore */ }
        }}
        className="px-3 py-1 bg-amber-600 text-white font-semibold text-xs hover:bg-amber-700"
      >
        {t('app_banner.reconnect_btn', 'Reconnect now')}
      </button>
    </div>
  );
}

const BatchCard = React.memo(function BatchCard({ tone, label, done, total, errors, running, onGoToImages, onCancel, onClear, openLabel }) {
  const { t } = useRuntime();
  const pct = Math.round((done / Math.max(1, total)) * 100);
  const toneCls = tone === 'apply'
    ? 'border-emerald-200 bg-emerald-50'
    : 'border-blue-200 bg-blue-50';
  return (
    <div className={`w-[320px] max-w-[90vw] border ${toneCls} shadow-md p-3 text-sm`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium truncate">{label}</span>
        <span className="text-xs text-gray-500">{(errors || []).length} {t('batch_banner.errors', 'errors')}</span>
      </div>
      <Progress value={pct} className="h-2" />
      <div className="flex items-center justify-between gap-3 mt-2 text-xs">
        <button onClick={onGoToImages} className="text-[#002FA7] hover:underline">{openLabel}</button>
        {running ? (
          <button onClick={onCancel} className="text-red-600 hover:underline">{t('common.cancel', 'Cancel')}</button>
        ) : (
          <button onClick={onClear} className="text-gray-500 hover:underline">{t('common.close', 'Close')}</button>
        )}
      </div>
    </div>
  );
});

function GlobalImageBatchBanner({ onGoToImages, onGoToCatalog }) {
  const {
    imageBatch, cancelImageBatch, clearImageBatch,
    imageApplyBatch, cancelApplyBatch, clearApplyBatch,
    contentBatch, cancelContentBatch, clearContentBatch,
  } = useApp();
  const { t } = useRuntime();
  if (!imageBatch && !imageApplyBatch && !contentBatch) return null;

  const bb = (key, fb) => t(`batch_banner.${key}`, fb);

  return (
    <div className="fixed top-3 right-3 z-50 space-y-2">
      {imageBatch && (
        <BatchCard
          tone="optimize"
          label={imageBatch.running
            ? `${bb('optimizing', 'Image optimization')}: ${imageBatch.done} / ${imageBatch.total}`
            : imageBatch.cancelled
              ? `${bb('optimizing_cancelled', 'Optimization cancelled')}: ${imageBatch.done} / ${imageBatch.total}`
              : `${bb('optimizing_completed', 'Optimization complete')}: ${imageBatch.done} / ${imageBatch.total}`}
          done={imageBatch.done}
          total={imageBatch.total}
          errors={imageBatch.errors}
          running={imageBatch.running}
          onGoToImages={onGoToImages}
          onCancel={cancelImageBatch}
          onClear={clearImageBatch}
          openLabel={bb('open_images', 'Open Images')}
        />
      )}
      {imageApplyBatch && (
        <BatchCard
          tone="apply"
          label={imageApplyBatch.running
            ? `${bb('applying', 'Applying to Shopify')}: ${imageApplyBatch.done} / ${imageApplyBatch.total}`
            : imageApplyBatch.cancelled
              ? `${bb('applying_cancelled', 'Application cancelled')}: ${imageApplyBatch.done} / ${imageApplyBatch.total}`
              : `${bb('applying_completed', 'Application complete')}: ${imageApplyBatch.done} / ${imageApplyBatch.total}`}
          done={imageApplyBatch.done}
          total={imageApplyBatch.total}
          errors={imageApplyBatch.errors}
          running={imageApplyBatch.running}
          onGoToImages={onGoToImages}
          onCancel={cancelApplyBatch}
          onClear={clearApplyBatch}
          openLabel={bb('open_images', 'Open Images')}
        />
      )}
      {contentBatch && (
        <BatchCard
          tone="optimize"
          label={(() => {
            const what = contentBatch.type === 'collection' ? bb('type_collections', 'Collections')
              : contentBatch.type === 'article' ? bb('type_articles', 'Articles')
              : contentBatch.type === 'page' ? bb('type_pages', 'Pages')
              : bb('type_contents', 'Content');
            const mode = contentBatch.mode === 'publish' ? bb('mode_publish', 'Publish')
              : contentBatch.mode === 'both' ? bb('mode_both', 'Generate+Publish')
              : bb('mode_generate', 'AI SEO Generation');
            const state = contentBatch.running ? ''
              : contentBatch.cancelled ? ` ${bb('state_cancelled', '(cancelled)')}`
              : ` ${bb('state_completed', '(completed)')}`;
            return `${what} · ${mode}${state}: ${contentBatch.done} / ${contentBatch.total}`;
          })()}
          done={contentBatch.done}
          total={contentBatch.total}
          errors={contentBatch.errors}
          running={contentBatch.running}
          onGoToImages={onGoToCatalog}
          openLabel={bb('open_catalog', 'Open Catalog')}
          onCancel={cancelContentBatch}
          onClear={clearContentBatch}
        />
      )}
    </div>
  );
}

function AppContent() {
  // Initial page resolved from URL (path, ?page= param, or localStorage)
  const [activePage, setActivePage] = useState(() => resolvePageFromUrl());
  const [purchaseCallback, setPurchaseCallback] = useState(null);
  const [subscriptionReturn, setSubscriptionReturn] = useState(null);

  // Proactively request a session token as soon as the app mounts inside
  // Shopify Admin. Shopify's "Session token detected" compliance check
  // monitors window.shopify.idToken() postMessage calls — requesting it
  // immediately on load guarantees the check is satisfied before any API
  // request fires through the axios interceptor.
  useEffect(() => {
    if (window.shopify?.idToken) {
      window.shopify.idToken().catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect Shopify billing return params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const purchaseId = params.get('purchase_id');
    const subReturn  = params.get('subscription_return');
    // Shopify App Pricing appends plan_handle when the merchant returns from
    // the hosted plan page. It does NOT send subscription_return — that is our
    // own parameter from the legacy flow — so the handle alone has to be
    // enough to trigger the check, or an App Pricing subscription is never
    // picked up. charge_id is the pre-2026-04-28 form, still sent by manual
    // pricing, and is accepted so a merchant mid-switchover is not stranded.
    const planHandle = params.get('plan_handle');
    const chargeId   = params.get('charge_id');
    if (purchaseId)  setPurchaseCallback({ purchaseId });
    if (subReturn || planHandle || chargeId) {
      setSubscriptionReturn({ planId: subReturn || planHandle || null, planHandle });
    }

    // Clean billing params from URL while keeping host/shop for App Bridge
    if (purchaseId || subReturn || planHandle || chargeId) {
      const clean = new URLSearchParams(window.location.search);
      clean.delete('purchase_id');
      clean.delete('subscription_return');
      clean.delete('plan_handle');
      clean.delete('charge_id');
      clean.delete('page');
      const qs = clean.toString() ? `?${clean.toString()}` : '';
      window.history.replaceState({}, '', `/settings${qs}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate: update React state + push URL (keeps host/shop params for App Bridge)
  const navigate = useCallback((page) => {
    setActivePage(page);
    if (page) {
      try { localStorage.setItem(LAST_ROUTE_KEY, page); } catch (_) {}
      window.dispatchEvent(new CustomEvent('aio:page-visit'));
      const path = pathForPage(page);
      // Preserve Shopify params (host, shop) while removing legacy ?page=
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      const qs = params.toString() ? `?${params.toString()}` : '';
      window.history.pushState({ page }, '', `${path}${qs}`);
    }
  }, []);

  // Browser back / forward
  useEffect(() => {
    const onPop = () => setActivePage(resolvePageFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Global navigation event dispatched by UpgradeModal CTA
  useEffect(() => {
    const onNav = (e) => { if (e.detail?.page) navigate(e.detail.page); };
    window.addEventListener('aio:navigate', onNav);
    return () => window.removeEventListener('aio:navigate', onNav);
  }, [navigate]);

  const PageComponent = pages[activePage] || Dashboard;

  return (
    <div className="flex min-h-screen bg-[#FDFDFD]">
      {/* React sidebar — always visible inside the app */}
      <Sidebar activePage={activePage} onPageChange={navigate} />

      <div className="flex-1 min-w-0 overflow-hidden">
        {/* Registers pages in Shopify Admin's native sidebar. No-op outside Shopify. */}
        <ShopifyNavMenu />

        <main>
          <TokenInvalidBanner />
          <div className="max-w-7xl mx-auto p-6 lg:p-8">
            <PageErrorBoundary key={activePage}>
              <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#002FA7]" /></div>}>
                <PageComponent
                  onNavigate={navigate}
                  purchaseCallback={activePage === 'settings' ? purchaseCallback : null}
                  onPurchaseHandled={() => setPurchaseCallback(null)}
                  subscriptionReturn={activePage === 'settings' ? subscriptionReturn : null}
                  onSubscriptionHandled={() => setSubscriptionReturn(null)}
                />
              </Suspense>
            </PageErrorBoundary>
          </div>
        </main>
      </div>

      <GlobalImageBatchBanner
        onGoToImages={() => navigate('images')}
        onGoToCatalog={() => navigate('products')}
      />
      <ReviewPopup />
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <AppProvider>
      <RuntimeProvider>
        <AppContent />
      </RuntimeProvider>
    </AppProvider>
  );
}

export default App;
